import React, { useEffect, useMemo, useState } from 'react';
import {
  Loader,
  RefreshCw,
  Search,
  AlertCircle,
  User,
  Phone,
  Mail,
  BadgeCheck,
  Calendar,
  CreditCard,
  ExternalLink,
  Clipboard,
  ChevronRight,
  AlertTriangle,
} from 'lucide-react';

/**
 * CustomerDashboard.tsx (Clientes)
 * - API /customers — muestra los registros como "Clientes" (customers, no contactos del directorio)
 * - Cada cliente muestra: avatar (iniciales), nombre, teléfono, email (si hay) y chip "Urgente" cuando aplique
 * - Panel de detalle con reservas, pagos, etc.
 */

// --- Config/API helpers -----------------------------------------------------
const CURRENCY_MXN = new Intl.NumberFormat('es-MX', {
  style: 'currency',
  currency: 'MXN',
  maximumFractionDigits: 0,
});

import { API_BASE_URL } from '@/config';

const BASE_URL = API_BASE_URL;
const API_KEY = 'prueba'; // mismo que lib/api.ts

async function apiFetch(path: string, init?: RequestInit) {
  const token = localStorage.getItem('auth_token');
  if (!token) {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('org_id');
    window.location.href = import.meta.env.BASE_URL;
    throw new Error('No authentication token');
  }
  const res = await fetch(`${BASE_URL}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'x-api-key': API_KEY,
      Authorization: `Bearer ${token}`,
      ...(init?.headers || {}),
    },
  });
  if (!res.ok) {
    if (res.status === 401) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('org_id');
      window.location.href = import.meta.env.BASE_URL;
      return;
    }
    let detail = `Request failed (${res.status})`;
    try {
      const data = await res.json();
      detail = data?.detail || JSON.stringify(data);
    } catch {}
    throw new Error(detail);
  }
  const ct = res.headers.get('content-type');
  return ct?.includes('application/json') ? res.json() : res.text();
}

// --- Tipos tolerantes al backend -------------------------------------------
export type CustomerBasic = {
  id: string;
  nombre?: string | null;
  telefono: string;
  email?: string | null;
  estatus_general?: string | null; // puede venir “urgente”, “alta”, etc.
  created_at?: string | null;
};

export type Payment = {
  id?: string;
  status?: string;
  amount?: number;
  currency?: string;
  payment_url?: string | null;
  receipt_url?: string | null;
  created_at?: string | null;
};

export type Booking = {
  id: string;
  org_id?: string;
  trip_id: string;
  trip_version_id?: string | null;
  start_date: string;
  status: string;
  currency?: string;
  subtotal?: number | null;
  total_price?: number | null;
  participants_json?: { type: 'adult' | 'child' | 'pet'; quantity: number }[];
  addons_json?: (string | { id: string; price?: number; charge_type?: string })[];
  pricing_snapshot?: {
    grand_total?: number;
    amount_due_now?: number;
    amount_remaining?: number;
    unit_prices?: { adult?: number; child?: number; pet?: number };
    addons_total?: number;
  };
  payments?: Payment[];
};

export type CustomerDetail = CustomerBasic & {
  leads?: any[];
  bookings?: Booking[];
  timeline_events?: any[];
};

// --- Utils UI ---------------------------------------------------------------
const statusChip = (status?: string) => {
  const s = (status || '').toUpperCase();
  const map: Record<string, string> = {
    PENDING_PAYMENT: 'bg-yellow-500/20 text-yellow-200 border border-yellow-500/30',
    REQUIRES_PAYMENT: 'bg-yellow-500/20 text-yellow-200 border border-yellow-500/30',
    PAID: 'bg-emerald-500/20 text-emerald-200 border border-emerald-500/30',
    CONFIRMED: 'bg-emerald-500/20 text-emerald-200 border border-emerald-500/30',
    CANCELED: 'bg-rose-500/20 text-rose-200 border border-rose-500/30',
    FAILED: 'bg-rose-500/20 text-rose-200 border border-rose-500/30',
    EXPIRED: 'bg-rose-500/20 text-rose-200 border border-rose-500/30',
    REFUNDED: 'bg-sky-500/20 text-sky-200 border border-sky-500/30',
  };
  return map[s] || 'bg-[color:var(--acc2)]/10 text-[color:var(--acc1)]/85 border border-[color:var(--acc1)]/20';
};

function parseISO(d?: string | null): Date | null {
  if (!d) return null;
  try { return new Date(d.endsWith('Z') ? d : `${d}T00:00:00Z`); } catch { return null; }
}

function peopleCounts(b?: Booking) {
  const out = { adult: 0, child: 0, pet: 0 } as Record<'adult'|'child'|'pet', number>;
  (b?.participants_json || []).forEach(p => { out[p.type] = (out[p.type] || 0) + (p.quantity || 0); });
  return out;
}

function latestPaymentURL(b?: Booking): string | undefined {
  const p = (b?.payments || []).slice().sort((a, b) => (parseISO(a.created_at)?.getTime() || 0) - (parseISO(b.created_at)?.getTime() || 0)).pop();
  return p?.payment_url || undefined;
}

// “Urgente” robusto desde estatus_general (acepta ‘urgente’, ‘urgent’, ‘alta’, ‘high’)
function isUrgentContact(c: CustomerBasic): boolean {
  const s = (c.estatus_general || '').toLowerCase();
  return ['urgente', 'urgent', 'alta', 'high', 'prioridad alta'].some(k => s.includes(k));
}

function initialsFrom(name?: string | null, telefono?: string) {
  const n = (name || '').trim();
  if (!n) return (telefono || '??').slice(-2);
  const parts = n.split(/\s+/).slice(0, 2);
  return parts.map(p => p[0]).join('').toUpperCase();
}

// --- CustomerBooking (detalle de una reserva) ------------------------------
export const CustomerBooking: React.FC<{ booking: Booking }> = ({ booking }) => {
  const counts = peopleCounts(booking);
  const ps = booking.pricing_snapshot || {};
  const amountTotal = ps.grand_total ?? booking.total_price ?? 0;
  const dueNow = ps.amount_due_now ?? amountTotal;
  const remaining = ps.amount_remaining ?? Math.max(0, amountTotal - dueNow);
  const payUrl = latestPaymentURL(booking);

  const handleCopy = async () => {
    if (!payUrl) return;
    try { await navigator.clipboard.writeText(payUrl); } catch {}
  };

  return (
    <div className="bg-[linear-gradient(135deg,var(--acc1),var(--acc2))] border border-[color:var(--acc1)]/25 rounded-lg p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-1">
          <div className="text-white font-semibold flex items-center gap-2">
            <Calendar className="h-4 w-4 text-[color:var(--acc1)]/90" />
            <span>{booking.start_date}</span>
          </div>
          <div className="text-[color:var(--acc1)]/90 text-sm">
            {booking.trip_id} / {booking.trip_version_id || '—'}
          </div>
        </div>
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusChip(booking.status)}`}>
          {booking.status}
        </span>
      </div>

      <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-4 text-white">
        <div>
          <div className="text-[color:var(--acc1)]/90 text-xs">Personas</div>
          <div className="font-semibold">
            Adultos: {counts.adult} · Niños: {counts.child} · Mascotas: {counts.pet}
          </div>
        </div>
        <div>
          <div className="text-[color:var(--acc1)]/90 text-xs">Total</div>
          <div className="font-semibold">{CURRENCY_MXN.format(amountTotal)}</div>
        </div>
        <div className="flex items-center gap-3">
          <div>
            <div className="text-[color:var(--acc1)]/90 text-xs">Anticipo</div>
            <div className="font-semibold">{CURRENCY_MXN.format(dueNow)}</div>
          </div>
          <div>
            <div className="text-[color:var(--acc1)]/90 text-xs">Restante</div>
            <div className="font-semibold">{CURRENCY_MXN.format(remaining)}</div>
          </div>
        </div>
      </div>

      {payUrl && (
        <div className="mt-4 flex flex-wrap gap-2">
          <a
            href={payUrl}
            target="_blank"
            className="inline-flex items-center gap-2 px-3 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg transition-colors"
          >
            <CreditCard className="h-4 w-4" /> Pagar ahora
            <ExternalLink className="h-4 w-4" />
          </a>
          <button
            onClick={handleCopy}
            className="inline-flex items-center gap-2 px-3 py-2 bg-[linear-gradient(135deg,var(--acc1),var(--acc2))] hover:opacity-90 text-white rounded-lg"
          >
            <Clipboard className="h-4 w-4" /> Copiar link
          </button>
        </div>
      )}

      {Array.isArray(booking.addons_json) && booking.addons_json.length > 0 && (
        <div className="mt-4 text-[color:var(--acc1)]/85 text-sm">
          <div className="text-[color:var(--acc1)]/90 text-xs mb-1">Extras</div>
          <ul className="list-disc ml-5">
            {booking.addons_json.map((a, idx) => (
              <li key={idx}>{typeof a === 'string' ? a : a.id}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

// --- CustomerDashboard (lista + detalle) -----------------------------------
const PER_PAGE = 20;

const fetchCustomers = async (skip = 0, limit = PER_PAGE, q?: string): Promise<CustomerBasic[]> => {
  const params = new URLSearchParams({ skip: String(skip), limit: String(limit) });
  if (q && q.trim()) params.append('q', q.trim());
  const data = await apiFetch(`/customers/?${params.toString()}`);
  return Array.isArray(data) ? data : [];
};

const fetchCustomerDetail = async (id: string): Promise<CustomerDetail> => {
  return apiFetch(`/customers/${id}`);
};

// Avatar circular con iniciales
const Avatar: React.FC<{ name?: string | null; phone?: string; urgent?: boolean }> = ({ name, phone, urgent }) => {
  const initials = initialsFrom(name, phone);
  return (
    <div className={`relative w-10 h-10 rounded-full grid place-items-center text-sm font-semibold
      ${urgent ? 'bg-rose-500/15 text-rose-200 border border-rose-400/40' : 'bg-white/10 text-white/90 border border-white/15'}`}>
      {initials}
      {urgent && (
        <span className="absolute -top-1 -right-1 inline-flex items-center justify-center w-4 h-4 rounded-full bg-rose-500 text-[10px]">
          !
        </span>
      )}
    </div>
  );
};

const ContactRow: React.FC<{
  c: CustomerBasic;
  active: boolean;
  onClick: () => void;
}> = ({ c, active, onClick }) => {
  const urgent = isUrgentContact(c);
  return (
    <button
      onClick={onClick}
      className={[
        'w-full text-left rounded-xl px-3 py-2 transition border',
        active ? 'border-cyan-300/60 bg-white/5 ring-1 ring-cyan-300/40' : 'border-white/10 hover:bg-white/5',
      ].join(' ')}
      aria-label="Abrir cliente"
    >
      <div className="flex items-center gap-3">
        <Avatar name={c.nombre} phone={c.telefono} urgent={urgent} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <div className="truncate font-medium">{c.nombre || c.telefono || 'Cliente'}</div>
            {urgent && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] rounded-full border border-rose-400/50 text-rose-300">
                <AlertTriangle className="w-3 h-3" /> Urgente
              </span>
            )}
          </div>
          <div className="text-sm opacity-80 flex items-center gap-3">
            <span className="inline-flex items-center gap-1"><Phone className="w-3.5 h-3.5" />{c.telefono}</span>
            {c.email && <span className="inline-flex items-center gap-1"><Mail className="w-3.5 h-3.5" />{c.email}</span>}
          </div>
        </div>
        <ChevronRight className="h-4 w-4 opacity-60" />
      </div>
    </button>
  );
};

const DetailsPane: React.FC<{ selected?: CustomerBasic | null; }> = ({ selected }) => {
  const [detail, setDetail] = useState<CustomerDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let abort = false;
    async function run() {
      if (!selected) { setDetail(null); return; }
      setLoading(true); setError(null);
      try {
        const d = await fetchCustomerDetail(selected.id);
        if (!abort) setDetail(d);
      } catch (e: any) {
        if (!abort) setError(e?.message || 'Error al cargar detalle');
      } finally { if (!abort) setLoading(false); }
    }
    run();
    return () => { abort = true; };
  }, [selected?.id]);

  if (!selected) {
    return (
      <div className="border border-white/10 rounded-xl p-6 opacity-80">
        Selecciona un cliente para ver el detalle.
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center p-12 opacity-80">
        <Loader className="animate-spin h-10 w-10 mb-4" />
        <span>Cargando detalle…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="border border-rose-400/40 rounded-xl p-6 text-rose-200 bg-rose-500/10">
        <AlertCircle className="h-5 w-5 inline mr-2" />
        {error}
      </div>
    );
  }

  if (!detail) return null;

  const full = detail as CustomerDetail;
  const bookings = (full.bookings || []) as Booking[];
  const urgent = isUrgentContact(full);

  return (
    <div className="space-y-4">
      {/* Header del cliente */}
      <div className="rounded-xl p-5 border border-white/10 bg-white/5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <Avatar name={full.nombre} phone={full.telefono} urgent={urgent} />
            <div>
              <div className="font-semibold text-lg">{full.nombre || full.telefono || 'Cliente'}</div>
              <div className="text-sm opacity-80 flex items-center gap-3">
                <span className="inline-flex items-center gap-1"><Phone className="w-3.5 h-3.5" />{full.telefono}</span>
                {full.email && <span className="inline-flex items-center gap-1"><Mail className="w-3.5 h-3.5" />{full.email}</span>}
              </div>
            </div>
          </div>
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
            urgent ? 'border-rose-400/50 text-rose-300 bg-rose-500/10' : 'border-white/20 text-white/80 bg-white/5'
          } border`}>
            {urgent ? 'Urgente' : (full.estatus_general || '—')}
          </span>
        </div>
      </div>

      {/* Reservas del cliente */}
      <div className="space-y-3">
        <div className="flex items-center gap-2 opacity-90">
          <BadgeCheck className="h-4 w-4" />
          <span className="font-semibold">Reservas</span>
          <span className="opacity-70">({bookings.length})</span>
        </div>
        {bookings.length === 0 ? (
          <div className="border border-white/10 rounded-lg p-6 opacity-80">
            Este cliente no tiene reservas.
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3">
            {bookings.map((b) => (<CustomerBooking key={b.id} booking={b} />))}
          </div>
        )}
      </div>
    </div>
  );
};

// --- Dashboard principal ----------------------------------------------------
const CustomerDashboard: React.FC = () => {
  const [customers, setCustomers] = useState<CustomerBasic[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const data = await fetchCustomers((page - 1) * PER_PAGE, PER_PAGE, searchTerm);
      setCustomers(data);
      if (data.length && !selectedId) setSelectedId(data[0].id);
      if (data.length && selectedId && !data.find(c => c.id === selectedId)) {
        setSelectedId(data[0].id);
      }
    } catch (e: any) {
      setError(e?.message || 'Error cargando clientes');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-line */ }, [page]);
  useEffect(() => { const t = setTimeout(() => { setPage(1); load(); }, 300); return () => clearTimeout(t); /* eslint-disable-line */ }, [searchTerm]);

  const handleRefresh = async () => { setRefreshing(true); await load(); setRefreshing(false); };

  const selected = useMemo(() => customers.find(c => c.id === selectedId) || null, [customers, selectedId]);

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="rounded-xl p-6 flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-3 border border-gray-200 dark:border-white/10 bg-white/5">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <User className="h-6 w-6" /> Clientes
          </h2>
          <p className="text-sm opacity-80 mt-1">{customers.length} resultado{customers.length === 1 ? '' : 's'}</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleRefresh}
            disabled={refreshing || loading}
            className="px-4 py-2 rounded-lg border border-gray-300 dark:border-white/15 hover:bg-white/5 transition disabled:opacity-50 flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            <span>Actualizar</span>
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="mb-6 relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 opacity-70 h-5 w-5" />
        <input
          type="text"
          placeholder="Buscar por nombre, teléfono o email…"
          className="w-full rounded-lg pl-10 pr-10 py-3 border border-white/15 bg-transparent outline-none focus:ring-1 focus:ring-cyan-300/60"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
        />
        {searchTerm && (
          <button
            onClick={() => setSearchTerm('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 opacity-70 hover:opacity-100"
            aria-label="Limpiar búsqueda"
          >
            <AlertCircle className="h-5 w-5" />
          </button>
        )}
      </div>

      {/* Content split: clientes + detalle */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Lista de clientes */}
        <div className="lg:col-span-1 space-y-2">
          {loading ? (
            <div className="flex flex-col items-center justify-center p-12 opacity-80">
              <Loader className="animate-spin h-10 w-10 mb-4" />
              <span>Cargando clientes…</span>
            </div>
          ) : error ? (
            <div className="border border-rose-400/40 rounded-lg p-6 text-rose-200 bg-rose-500/10">
              <AlertCircle className="h-5 w-5 inline mr-2" />
              {error}
            </div>
          ) : customers.length === 0 ? (
            <div className="border border-white/10 rounded-lg p-8 text-center opacity-80">
              <AlertCircle className="h-10 w-10 mx-auto mb-4" />
              <p className="text-lg font-medium">No se encontraron clientes</p>
              <p className="mt-2">Intenta con otro término de búsqueda</p>
            </div>
          ) : (
            <div className="space-y-2">
              {customers.map(c => (
                <ContactRow key={c.id} c={c} active={c.id === selectedId} onClick={() => setSelectedId(c.id)} />
              ))}
            </div>
          )}

          {/* Paginación mínima (prev/next) basada en PER_PAGE */}
          {!loading && !error && (
            <div className="flex justify-between items-center gap-2 mt-4">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="opacity-80 disabled:opacity-40"
              >
                Anterior
              </button>
              <span className="opacity-80">Página {page}</span>
              <button
                onClick={() => setPage(p => p + 1)}
                className="opacity-80"
              >
                Siguiente
              </button>
            </div>
          )}
        </div>

        {/* Detalle del cliente */}
        <div className="lg:col-span-2">
          <DetailsPane selected={selected} />
        </div>
      </div>
    </div>
  );
};

export default CustomerDashboard;
